export function nameInitials(name, fallback = '?') {
  const raw = String(name || fallback).trim();
  const parts = raw.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return (raw.slice(0, 2) || fallback).toUpperCase();
}

export function formatPanelCurrency(amount) {
  if (amount === null || amount === undefined) return '$0.00';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
}

/** Format date as US MM/DD/YYYY */
export function formatUsDate(value) {
  if (!value) return '';
  const raw = String(value).trim();

  // Already ISO YYYY-MM-DD
  const isoMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoMatch) {
    return `${isoMatch[2]}/${isoMatch[3]}/${isoMatch[1]}`;
  }

  // Already US MM/DD/YYYY or M/D/YYYY
  const usMatch = raw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (usMatch) {
    return `${usMatch[1].padStart(2, '0')}/${usMatch[2].padStart(2, '0')}/${usMatch[3]}`;
  }

  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return raw;
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const year = date.getFullYear();
  return `${month}/${day}/${year}`;
}

/**
 * Convert period text like "8/1/2026 - 8/8/2026 (40 hrs)" to US dates.
 */
export function formatUsPeriodText(text) {
  if (!text) return '';
  return String(text).replace(
    /(\d{4}-\d{2}-\d{2}|\d{1,2}\/\d{1,2}\/\d{4})/g,
    (match) => formatUsDate(match)
  );
}

function startOfDay(date) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function dayDiffFromToday(date) {
  const ms = startOfDay(new Date()).getTime() - startOfDay(date).getTime();
  return Math.round(ms / (24 * 60 * 60 * 1000));
}

/** Outlook-style date in mail list row */
export function formatOutlookListDate(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';

  const now = new Date();
  const diffDays = dayDiffFromToday(date);

  if (diffDays === 0) {
    return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  }
  if (diffDays === 1) {
    return 'Yesterday';
  }
  if (diffDays > 1 && diffDays < 7) {
    return date.toLocaleDateString('en-US', { weekday: 'short' });
  }
  if (date.getFullYear() === now.getFullYear()) {
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }
  return date.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: '2-digit' });
}

/** Outlook-style section header for grouped inbox */
export function formatOutlookGroupLabel(value) {
  if (!value) return 'Older';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Older';

  const now = new Date();
  const diffDays = dayDiffFromToday(date);

  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays > 1 && diffDays < 7) {
    return date.toLocaleDateString('en-US', { weekday: 'long' });
  }
  if (diffDays >= 7 && diffDays < 14) return 'Last week';
  if (date.getFullYear() === now.getFullYear()) {
    return date.toLocaleDateString('en-US', { month: 'long' });
  }
  return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
}

/** Outlook-style date in message detail header */
export function formatOutlookDetailDate(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';

  const now = new Date();
  const diffDays = dayDiffFromToday(date);
  const time = date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });

  if (diffDays === 0) return `Today ${time}`;
  if (diffDays === 1) return `Yesterday ${time}`;
  if (diffDays > 1 && diffDays < 7) {
    const day = date.toLocaleDateString('en-US', { weekday: 'long' });
    return `${day} ${time}`;
  }
  return date.toLocaleString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
    hour: 'numeric',
    minute: '2-digit',
  });
}

/** Parse email address header string into { name, email }[] */
export function parseAddressHeaderString(value) {
  if (!value || typeof value !== 'string') return [];

  return value
    .split(/[,;]+/)
    .map((part) => part.trim())
    .filter(Boolean)
    .map((part) => {
      const angleMatch = part.match(/^(?:"([^"]+)"|([^<]*?))\s*<([^>]+)>$/);
      if (angleMatch) {
        const name = (angleMatch[1] || angleMatch[2] || '').trim();
        const email = angleMatch[3].trim();
        return { name: name || email.split('@')[0], email };
      }
      if (part.includes('@')) {
        return { name: part.split('@')[0], email: part };
      }
      return null;
    })
    .filter(Boolean);
}

/** Resolve To/Cc recipients from structured API data or header strings */
export function resolveEmailRecipients(email, field = 'to') {
  const arrayKey = field === 'cc' ? 'ccRecipients' : 'toRecipients';
  if (Array.isArray(email?.[arrayKey]) && email[arrayKey].length) {
    return email[arrayKey].filter((item) => item?.email);
  }
  return parseAddressHeaderString(email?.[field]);
}

/** Soft Outlook-style avatar colors (pastel bg + readable text) */
const AVATAR_PALETTE = [
  { backgroundColor: '#ede4f7', color: '#5c2d91' },
  { backgroundColor: '#d5f0ed', color: '#0e7873' },
  { backgroundColor: '#deecf9', color: '#005a9e' },
  { backgroundColor: '#fce8f3', color: '#9b0062' },
  { backgroundColor: '#fff0e0', color: '#8f4f00' },
  { backgroundColor: '#e8f4ea', color: '#0b6a0b' },
  { backgroundColor: '#eef0ff', color: '#444791' },
  { backgroundColor: '#f3f2f1', color: '#605e5c' },
];

function hashEmail(email) {
  const value = String(email || '');
  let hash = 0;
  for (let i = 0; i < value.length; i += 1) {
    hash = value.charCodeAt(i) + ((hash << 5) - hash);
  }
  return Math.abs(hash);
}

/** @deprecated use recipientAvatarStyle */
export function recipientAvatarColor(email) {
  return recipientAvatarStyle(email).backgroundColor;
}

/** Outlook-style soft avatar style object */
export function recipientAvatarStyle(email) {
  return AVATAR_PALETTE[hashEmail(email) % AVATAR_PALETTE.length];
}

export function recipientDisplayName(recipient) {
  if (!recipient) return '';
  return recipient.name || recipient.email || '';
}
